import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from '../../components/Sidebar';
import ProfileDropdown from '../../components/ProfileDropdown';
import API_BASE from '../../utils/api';
import { fetchDefaultAcademicYear, computedAcademicYearFallback } from '../../utils/academicYear';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DEFAULT_PERIODS = [
  { periodNumber: 1, subject: '', teacherName: '', startTime: '09:00', endTime: '09:45', room: '' },
  { periodNumber: 2, subject: '', teacherName: '', startTime: '09:45', endTime: '10:30', room: '' },
  { periodNumber: 3, subject: '', teacherName: '', startTime: '10:45', endTime: '11:30', room: '' },
  { periodNumber: 4, subject: '', teacherName: '', startTime: '11:30', endTime: '12:15', room: '' },
  { periodNumber: 5, subject: '', teacherName: '', startTime: '13:00', endTime: '13:45', room: '' },
  { periodNumber: 6, subject: '', teacherName: '', startTime: '13:45', endTime: '14:30', room: '' },
  { periodNumber: 7, subject: '', teacherName: '', startTime: '14:45', endTime: '15:30', room: '' },
];

const DAY_COLORS = {
  Monday: 'bg-blue-500',
  Tuesday: 'bg-violet-500',
  Wednesday: 'bg-emerald-500',
  Thursday: 'bg-amber-500',
  Friday: 'bg-rose-500',
  Saturday: 'bg-cyan-500',
};

const DAY_BADGES = {
  Monday: 'bg-blue-100 text-blue-800 border-blue-200',
  Tuesday: 'bg-violet-100 text-violet-800 border-violet-200',
  Wednesday: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  Thursday: 'bg-amber-100 text-amber-800 border-amber-200',
  Friday: 'bg-rose-100 text-rose-800 border-rose-200',
  Saturday: 'bg-cyan-100 text-cyan-800 border-cyan-200',
};

const formatCN = (cn) => /^\d+$/.test(String(cn || '')) ? `Class ${cn}` : (cn || '');

const PrincipalTimetable = () => {
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [timetables, setTimetables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [viewTimetable, setViewTimetable] = useState(null);

  const getMonday = (d) => {
    const date = new Date(d);
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    date.setDate(diff);
    date.setHours(0, 0, 0, 0);
    return date;
  };

  const getSaturday = (monday) => {
    const sat = new Date(monday);
    sat.setDate(sat.getDate() + 5);
    return sat;
  };

  const [formData, setFormData] = useState({
    classId: '',
    academicYear: computedAcademicYearFallback(),
    weekStartDate: getMonday(new Date()).toISOString().split('T')[0],
    weekEndDate: getSaturday(getMonday(new Date())).toISOString().split('T')[0],
    schedule: DAYS.map(day => ({ day, periods: DEFAULT_PERIODS.map(p => ({ ...p })) }))
  });

  useEffect(() => {
    fetchClasses();
    fetchTeachers();
    // Only overwrite the seeded fallback while the admin hasn't started
    // editing this field themselves.
    fetchDefaultAcademicYear().then((year) => {
      setFormData((prev) => (prev.academicYear === computedAcademicYearFallback() ? { ...prev, academicYear: year } : prev));
    });
  }, []);
  useEffect(() => { if (selectedClass) fetchTimetables(); }, [selectedClass]);

  const fetchClasses = async () => {
    try {
      const res = await axios.get(`${API_BASE}/classes`);
      setClasses(res.data);
      if (res.data.length > 0) setSelectedClass(res.data[0]._id);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchTeachers = async () => {
    try {
      const res = await axios.get(`${API_BASE}/teachers`);
      setTeachers(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchTimetables = async () => {
    try {
      const res = await axios.get(`${API_BASE}/timetable?classId=${selectedClass}`);
      setTimetables(res.data);
    } catch (err) { console.error(err); }
  };

  const openCreateModal = () => {
    setFormData(prev => ({
      ...prev,
      classId: selectedClass,
      schedule: DAYS.map(day => ({ day, periods: DEFAULT_PERIODS.map(p => ({ ...p })) }))
    }));
    setShowModal(true);
    setError('');
  };

  const updatePeriod = (dayIdx, periodIdx, field, value) => {
    setFormData(prev => {
      const newSchedule = [...prev.schedule];
      newSchedule[dayIdx] = { ...newSchedule[dayIdx], periods: [...newSchedule[dayIdx].periods] };
      newSchedule[dayIdx].periods[periodIdx] = { ...newSchedule[dayIdx].periods[periodIdx], [field]: value };
      return { ...prev, schedule: newSchedule };
    });
  };

  const addPeriod = (dayIdx) => {
    setFormData(prev => {
      const newSchedule = [...prev.schedule];
      const periods = newSchedule[dayIdx].periods;
      newSchedule[dayIdx] = {
        ...newSchedule[dayIdx],
        periods: [...periods, { periodNumber: periods.length + 1, subject: '', teacherName: '', startTime: '', endTime: '', room: '' }]
      };
      return { ...prev, schedule: newSchedule };
    });
  };

  const removePeriod = (dayIdx, periodIdx) => {
    setFormData(prev => {
      const newSchedule = [...prev.schedule];
      const periods = newSchedule[dayIdx].periods.filter((_, i) => i !== periodIdx)
        .map((p, i) => ({ ...p, periodNumber: i + 1 }));
      newSchedule[dayIdx] = { ...newSchedule[dayIdx], periods };
      return { ...prev, schedule: newSchedule };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    try {
      await axios.post(`${API_BASE}/timetable`, formData);
      setSuccess('Timetable saved successfully!');
      setShowModal(false);
      fetchTimetables();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save timetable');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this timetable?')) return;
    try {
      await axios.delete(`${API_BASE}/timetable/${id}`);
      fetchTimetables();
      setSuccess('Timetable deleted');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError('Failed to delete'); }
  };

  const handleDuplicate = async (id) => {
    try {
      await axios.post(`${API_BASE}/timetable/${id}/duplicate`);
      fetchTimetables();
      setSuccess('Timetable duplicated to next week!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to duplicate');
    }
  };

  const formatDate = (d) => new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

  const updateWeekStart = (dateStr) => {
    const monday = getMonday(new Date(dateStr));
    const saturday = getSaturday(monday);
    setFormData(prev => ({
      ...prev,
      weekStartDate: monday.toISOString().split('T')[0],
      weekEndDate: saturday.toISOString().split('T')[0]
    }));
  };

  const totalPeriods = (tt) => tt.schedule.reduce((sum, d) => sum + d.periods.length, 0);

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <nav className="bg-white shadow-sm sticky top-0 z-10">
          <div className="px-6 py-4 flex justify-between items-center">
            <h1 className="text-2xl font-semibold text-gray-800">Timetable Management</h1>
            <ProfileDropdown />
          </div>
        </nav>

        <div className="flex-1 overflow-auto p-6">
          <div className="space-y-5">
          {success && (
            <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-xl flex items-center gap-2 shadow-sm">
              <span className="text-green-500 font-bold">✓</span> {success}
            </div>
          )}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-xl flex items-center gap-2 shadow-sm">
              <span className="text-red-500 font-bold">✕</span> {error}
            </div>
          )}

          {/* Class Selector + Toolbar */}
          <div className="bg-white rounded-lg shadow p-4 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
            <div className="flex flex-wrap gap-2 items-center">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider mr-1">Class:</span>
              {classes.map(c => (
                <button
                  key={c._id}
                  onClick={() => setSelectedClass(c._id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition border ${
                    selectedClass === c._id
                      ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-300 hover:text-indigo-600'
                  }`}
                >
                  {formatCN(c.className)} – {c.section}
                </button>
              ))}
            </div>
            <button
              onClick={openCreateModal}
              className="bg-indigo-600 text-white px-5 py-2 rounded-lg hover:bg-indigo-700 flex items-center gap-2 whitespace-nowrap text-sm font-medium"
            >
              <span className="text-lg leading-none">+</span> Create Timetable
            </button>
          </div>

          {/* Timetable List */}
          {loading ? (
            <div className="text-center py-16">
              <div className="animate-spin inline-block w-10 h-10 border-4 border-indigo-200 border-t-indigo-600 rounded-full mb-3"></div>
              <p className="text-gray-400 text-sm">Loading timetables...</p>
            </div>
          ) : timetables.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-14 text-center">
              <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-700 mb-1">No Timetables Yet</h3>
              <p className="text-gray-400 text-sm mb-5">Create the first weekly timetable for this class</p>
              <button
                onClick={openCreateModal}
                className="px-5 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium transition shadow-sm"
              >
                + Create Timetable
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {timetables.map(tt => (
                <div key={tt._id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  {/* Card Header */}
                  <div className="px-5 py-4 flex flex-wrap justify-between items-center gap-3 border-b border-gray-100">
                    <div className="flex items-center gap-4">
                      <div className="w-11 h-11 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0 shadow-sm">
                        {formatCN(tt.classId?.className).replace('Class ', '')}
                        {tt.classId?.section}
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-800 text-base">
                          {formatCN(tt.classId?.className)} – {tt.classId?.section}
                        </h3>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {formatDate(tt.weekStartDate)} → {formatDate(tt.weekEndDate)}
                          &nbsp;·&nbsp;AY {tt.academicYear}
                          &nbsp;·&nbsp;<span className="font-medium text-indigo-500">{totalPeriods(tt)} periods/week</span>
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <button
                        onClick={() => setViewTimetable(viewTimetable?._id === tt._id ? null : tt)}
                        className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition ${
                          viewTimetable?._id === tt._id
                            ? 'bg-indigo-600 text-white'
                            : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
                        }`}
                      >
                        {viewTimetable?._id === tt._id ? 'Hide Schedule' : 'View Schedule'}
                      </button>
                      <button
                        onClick={() => handleDuplicate(tt._id)}
                        className="px-3 py-1.5 text-xs font-semibold bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 transition"
                      >
                        Duplicate Next Week
                      </button>
                      <button
                        onClick={() => handleDelete(tt._id)}
                        className="px-3 py-1.5 text-xs font-semibold bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition"
                      >
                        Delete
                      </button>
                    </div>
                  </div>

                  {/* Day Summary Badges (collapsed) */}
                  {viewTimetable?._id !== tt._id && (
                    <div className="px-5 py-3 flex flex-wrap gap-2">
                      {tt.schedule.map(day => (
                        <span
                          key={day.day}
                          className={`px-3 py-1 rounded-full text-xs font-semibold border ${DAY_BADGES[day.day] || 'bg-gray-100 text-gray-700 border-gray-200'}`}
                        >
                          {day.day.slice(0, 3)} · {day.periods.length}P
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Expanded Schedule View */}
                  {viewTimetable?._id === tt._id && (
                    <div className="p-5 space-y-3">
                      {tt.schedule.map(daySchedule => (
                        <div key={daySchedule.day} className="rounded-xl overflow-hidden border border-gray-100">
                          <div className={`px-4 py-2.5 flex items-center gap-2 ${DAY_COLORS[daySchedule.day] || 'bg-gray-500'} text-white`}>
                            <span className="font-bold text-sm">{daySchedule.day}</span>
                            <span className="text-white/60 text-xs">({daySchedule.periods.length} period{daySchedule.periods.length !== 1 ? 's' : ''})</span>
                          </div>
                          <table className="w-full text-sm">
                            <thead className="bg-gray-50 border-b border-gray-100">
                              <tr>
                                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-400 w-10">#</th>
                                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-400">Subject</th>
                                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-400">Teacher</th>
                                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-400">Time</th>
                                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-400">Room</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                              {daySchedule.periods.map((p, i) => (
                                <tr key={i} className="hover:bg-gray-50 transition">
                                  <td className="px-4 py-2.5 font-medium text-gray-300 text-xs">{p.periodNumber}</td>
                                  <td className="px-4 py-2.5">
                                    {p.subject
                                      ? <span className="font-semibold text-gray-800">{p.subject}</span>
                                      : <span className="text-gray-300 text-xs italic">—</span>}
                                  </td>
                                  <td className="px-4 py-2.5 text-gray-600 text-sm">
                                    {p.teacherName || <span className="text-gray-300 text-xs">—</span>}
                                  </td>
                                  <td className="px-4 py-2.5 text-gray-400 text-xs font-mono">{p.startTime} – {p.endTime}</td>
                                  <td className="px-4 py-2.5 text-gray-400 text-xs">{p.room || <span className="text-gray-200">—</span>}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          </div>
        </div>

        {/* Create Timetable Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-4 overflow-y-auto">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl my-6">
              <div className="bg-gradient-to-r from-indigo-700 to-purple-700 px-6 py-5 rounded-t-2xl flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-bold text-white">Create Weekly Timetable</h2>
                  <p className="text-indigo-200 text-sm mt-0.5">Schedule periods for each day of the week</p>
                </div>
                <button onClick={() => setShowModal(false)} className="text-white hover:text-indigo-200 text-2xl leading-none transition">&times;</button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-5 max-h-[80vh] overflow-y-auto">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
                )}

                {/* Config Row */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Class</label>
                    <select
                      value={formData.classId}
                      onChange={e => setFormData(prev => ({ ...prev, classId: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white text-sm"
                    >
                      {classes.map(c => <option key={c._id} value={c._id}>{formatCN(c.className)} – {c.section}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Academic Year</label>
                    <input
                      type="text"
                      value={formData.academicYear}
                      onChange={e => setFormData(prev => ({ ...prev, academicYear: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Week Starting (Monday)</label>
                    <input
                      type="date"
                      value={formData.weekStartDate}
                      onChange={e => updateWeekStart(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white text-sm"
                    />
                    <p className="text-xs text-gray-400 mt-1">{formData.weekStartDate} → {formData.weekEndDate}</p>
                  </div>
                </div>

                {/* Day-wise Period Editor */}
                {formData.schedule.map((daySchedule, dayIdx) => (
                  <div key={daySchedule.day} className="rounded-xl overflow-hidden border border-gray-200">
                    <div className={`px-4 py-3 flex justify-between items-center ${DAY_COLORS[daySchedule.day] || 'bg-gray-500'} text-white`}>
                      <h4 className="font-bold text-sm">{daySchedule.day}</h4>
                      <button
                        type="button"
                        onClick={() => addPeriod(dayIdx)}
                        className="text-xs px-3 py-1 bg-white/20 hover:bg-white/30 rounded-full transition text-white font-medium"
                      >
                        + Add Period
                      </button>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b border-gray-100">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs text-gray-400 font-semibold w-10">#</th>
                            <th className="px-3 py-2 text-left text-xs text-gray-400 font-semibold">Subject</th>
                            <th className="px-3 py-2 text-left text-xs text-gray-400 font-semibold">Teacher</th>
                            <th className="px-3 py-2 text-left text-xs text-gray-400 font-semibold w-28">Start</th>
                            <th className="px-3 py-2 text-left text-xs text-gray-400 font-semibold w-28">End</th>
                            <th className="px-3 py-2 text-left text-xs text-gray-400 font-semibold w-24">Room</th>
                            <th className="px-3 py-2 w-8"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {daySchedule.periods.map((period, pIdx) => (
                            <tr key={pIdx} className="hover:bg-gray-50">
                              <td className="px-3 py-2 font-medium text-gray-300 text-xs">{period.periodNumber}</td>
                              <td className="px-3 py-2">
                                <input
                                  type="text"
                                  value={period.subject}
                                  placeholder="Subject"
                                  onChange={e => updatePeriod(dayIdx, pIdx, 'subject', e.target.value)}
                                  className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                                />
                              </td>
                              <td className="px-3 py-2">
                                <select
                                  value={period.teacherName}
                                  onChange={e => updatePeriod(dayIdx, pIdx, 'teacherName', e.target.value)}
                                  className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
                                >
                                  <option value="">— Select Teacher —</option>
                                  {teachers.map(t => (
                                    <option key={t._id} value={t.name}>{t.name}</option>
                                  ))}
                                </select>
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  type="time"
                                  value={period.startTime}
                                  onChange={e => updatePeriod(dayIdx, pIdx, 'startTime', e.target.value)}
                                  className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                                />
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  type="time"
                                  value={period.endTime}
                                  onChange={e => updatePeriod(dayIdx, pIdx, 'endTime', e.target.value)}
                                  className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                                />
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  type="text"
                                  value={period.room}
                                  placeholder="Room"
                                  onChange={e => updatePeriod(dayIdx, pIdx, 'room', e.target.value)}
                                  className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                                />
                              </td>
                              <td className="px-3 py-2">
                                {daySchedule.periods.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => removePeriod(dayIdx, pIdx)}
                                    className="text-gray-300 hover:text-red-500 text-xl leading-none transition"
                                  >
                                    ×
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}

                <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-5 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600 text-sm font-medium transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-semibold shadow transition"
                  >
                    Save Timetable
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrincipalTimetable;
